import React, { useState, useEffect, useMemo } from "react";
import GameHistoryService from "../../../service/GameHistoryService";
import { addDays, endOfDay, startOfDay } from "date-fns";
import { useTranslation } from "react-i18next";

import { copyText, formatDate } from "../../../helpers/functions";
import { SectionLoader } from "../../../components/ui/loaders/SectionLoader";
import { CONFIG } from "../../../helpers/constants";
import moment from "moment-timezone";
import CustomTable from "../../../components/ui/tables/CustomTable";
import { FaCopy } from "react-icons/fa";
import { ReactComponent as IconSearch } from "../../../assets/icons/icon-search.svg";

const UserCasinoBets = () => {
  let currDay = addDays(new Date(), 0);
  const { t } = useTranslation();

  const gameHistoryService = new GameHistoryService(CONFIG.BASE_URL);
  const [dateStart, setDateStart] = useState(
    formatDate(addDays(new Date(), -30))
  );
  const [dateEnd, setDateEnd] = useState(formatDate(new Date()));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [providers, setProviders] = useState([]);
  // const [selectedProvider, setSelectedProvider] = useState("All");

  // const itemsPerPage = 50;

  // Fetch game history
  const fetchHistory = async () => {
    if (!dateStart || !dateEnd) {
      setError("Please select both start and end dates.");
      return;
    }

    setError(null);
    try {
      setLoading(true);
      const { data } = await gameHistoryService.fetchGameHistory({
        date_start: startOfDay(dateStart),
        date_end: endOfDay(dateEnd),
        page_number: 1,
        items_per_page: 50,
      });

      setData(data);

      // Extract unique providers from the data
      const uniqueProviders = [
        ...new Set(
          data.map((item) => {
            const match = item.gameName.match(/\(([^)]+)\)$/);
            return match ? match[1] : "Unknown";
          })
        ),
      ];
      setProviders(["All", ...uniqueProviders]);
    } catch (err) {
      setError(err.message || "An error occurred while fetching game history.");
    } finally {
      setLoading(false);
    }
  };

  // Filter the table based on search query and provider
  // useEffect(() => {
  //   const lowerCaseQuery = searchQuery.toLowerCase();
  //   const filtered = history.filter((item) => {
  //     const matchesQuery = item.gameName.toLowerCase().includes(lowerCaseQuery);
  //     const matchesProvider = selectedProvider === "All" || item.gameName.includes(`(${selectedProvider})`);
  //     return matchesQuery && matchesProvider;
  //   });
  //   setFilteredHistory(filtered);
  //   setCurrentPage(1); // Reset to the first page when filtering
  // }, [searchQuery, selectedProvider, history]);

  // Paginate the table
  // const indexOfLastItem = currentPage * itemsPerPage;
  // const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  // const currentItems = filteredHistory.slice(indexOfFirstItem, indexOfLastItem);
  // const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);

  // const handlePageChange = (page) => {
  //   setCurrentPage(page);
  // };

  const tableData = data?.map((item, index) => {
    return {
      ...item,
    };
  });

  const tableColumns = useMemo(
    () => [
      {
        accessorKey: "transaction_id",
        header: "TransactionID",
        enableHiding: false,
        size: 50,
        Cell: ({ cell }) => (
          <div className="flex">
            <div
              className="truncate max-w-24"
              title={cell.row.original.transaction_id}
            >
              {cell.row.original.transaction_id}
            </div>
            <span
              className="text-primary cursor-pointer"
              onClick={() => copyText(cell.row.original.transaction_id)}
            >
              <FaCopy />
            </span>
          </div>
        ),
      },

      {
        accessorKey: "gameName",
        header: "Game Name",
        enableHiding: false,
        size: 50,
        Cell: ({ cell }) => (
          <div className="truncate max-w-36" title={cell.row.original.gameName}>
            {cell.row.original.gameName}
          </div>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        enableHiding: false,
        size: 50,
        Cell: ({ cell }) => (
          <>
            {cell.row.original.status === "credit"
              ? "Win"
              : cell.row.original.status === "debit"
              ? "Bet"
              : cell.row.original.status || "-"}
          </>
        ),
      },

      {
        accessorKey: "amount",
        header: "Amount",
        enableHiding: false,
        size: 50,
        Cell: ({ cell }) => Math.abs(cell.row.original.amount).toFixed(2),
      },
      {
        accessorKey: "balanceBefore.sender",
        header: "Bal. Before",
        size: 50,
        Cell: ({ cell }) => (
          <>
            {cell.row.original.balanceBefore?.sender != null
              ? Math.abs(cell.row.original.balanceBefore.sender).toFixed(2)
              : "N/A"}
          </>
        ),
      },
      {
        accessorKey: "balanceAfter.sender",
        header: "Bal. After",
        size: 50,
        Cell: ({ cell }) => (
          <>
            {cell.row.original.balanceAfter?.sender != null
              ? Math.abs(cell.row.original.balanceAfter.sender).toFixed(2)
              : "N/A"}
          </>
        ),
      },

      {
        accessorKey: "date",
        header: "Date",
        enableHiding: false,
        size: 50,
        Cell: ({ cell }) =>
          moment
            ?.tz(cell.row.original?.date, "UTC")
            ?.format("YYYY-MM-DD HH:mm:ss"),
      },
    ],
    []
  );

  return (
    <div className="text-white md:px-5 md:py-4 p-2 bg-[#000c24] lg:max-w-screen-xl max-w-screen-md md:bg-transparent mx-auto">
      <h1 className="md:text-[26px] text-[20px] font-semibold md:mb-6 mb-4 text-center md:text-left md:capitalize uppercase">
        {t("Casino Bets")}
      </h1>
      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex flex-col w-full">
          <label className="text-gray-400 font-medium mb-2">
            {t("Start Date")}
          </label>
          <input
            type="date"
            className="border border-gray-600 bg-white text-black rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-400"
            value={dateStart}
            max={dateEnd}
            onChange={(e) => setDateStart(e.target.value)}
          />
        </div>
        <div className="flex flex-col w-full">
          <label className="text-gray-400 font-medium mb-2">
            {t("End Date")}
          </label>
          <input
            type="date"
            className="border border-gray-600 bg-white text-black rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-400"
            value={dateEnd}
            min={dateStart}
            max={currDay}
            onChange={(e) => setDateEnd(e.target.value)}
          />
        </div>
      </div>

      {/* Fetch Button */}
      <div className="flex gap-4 mb-5">
        <button
          onClick={fetchHistory}
          disabled={loading}
          className="bg-primary/80 text-gray-900 py-2 px-6 rounded-lg hover:bg-primary/70 transition disabled:opacity-50 disabled:cursor-not-allowed w-full"
        >
          {loading ? "LOADING..." : "SEARCH"}
        </button>
        <button
          onClick={() => {
            setData([]);
          }}
          disabled={loading}
          className=" text-white py-2 px-6 rounded-lg border border-white border-opacity-20 hover:border-primary hover:border-opacity-100 transition disabled:opacity-50 disabled:cursor-not-allowed md:w-96 w-full"
        >
          {t("CLEAR")}
        </button>
      </div>

      {/* Error Message */}
      {error && <p className="text-red-400 mt-4 text-center">{error}</p>}

      {!loading ? (
        data && data.length ? (
          <CustomTable
            darkMode
            noWrap
            columns={tableColumns}
            data={tableData ? tableData : []}
            enableGlobalFilter={true}
            enableTopToolbar={true}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-6 px-3 my-7">
            <div className="border border-white/10 rounded-lg p-6 sm:p-8 [&_svg_path]:fill-slate-500">
              <IconSearch />
            </div>
            <span className="text-lg sm:text-xl mt-8 mb-4 text-[#474747] text-center w-full max-w-[300px]">
              Search all available records by selecting one of the filters
              above!
            </span>
          </div>
        )
      ) : (
        <div className="flex justify-center py-8">
          <SectionLoader bgColor="dark" />
        </div>
      )}
    </div>
  );
};

export default UserCasinoBets;
